import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import type { SalesCommissionPoint } from '../../lib/api';
import { formatPeso } from './format';
import { TenantWideBadge } from './tenant-wide-badge';

/**
 * Stacked bars over the last 12 weeks: sales (foreground ink) and commission
 * paid (debit-crimson, money out) per week. Sales sit on the bottom;
 * commission stacks on top so the operator sees the gross + commission-as-
 * fraction at a glance. Sky stays off the chart (One Accent Rule); the money
 * pair carries the meaning instead.
 *
 * UNDER A BRANCH FILTER the bars go side-by-side instead of stacked: sales
 * narrow to the branch, commissions can't (no branch column on
 * `ledger_entries`), and stacking two different scopes claims a whole/part
 * relationship that isn't true. Grouped bars claim nothing.
 */
const TOOLTIP_STYLE = {
  background: 'hsl(var(--popover))',
  color: 'hsl(var(--popover-foreground))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 6,
  boxShadow: 'var(--shadow-elevated)',
  fontSize: 12,
} as const;
export function SalesCommissionChart({
  series,
  caption = 'Last 12 weeks',
  filtered = false,
}: {
  series: SalesCommissionPoint[];
  caption?: string;
  filtered?: boolean;
}) {
  const data = series.map((s, i) => ({
    label: `W${i + 1}`,
    sales: Number(s.sales),
    commission: Number(s.commission),
  }));
  // undefined = grouped bars; recharts stacks only when the ids match.
  const stackId = filtered ? undefined : 'a';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-h3">
          <span>
            Sales vs commission paid{' '}
            <span className="font-normal text-caption text-muted-foreground">{caption}</span>
          </span>
          <TenantWideBadge
            show={filtered}
            label="Commissions: all branches"
            title="Sales narrow to the selected branch; commissions have no branch dimension, so the bars are shown side by side rather than stacked."
          />
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 py-2">
        <div className="h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 2" vertical={false} />
              <XAxis
                dataKey="label"
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickMargin={6}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                width={44}
                tickFormatter={(v: number) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))}
              />
              <Tooltip
                cursor={{ fill: 'hsl(var(--muted))' }}
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: 'hsl(var(--muted-foreground))' }}
                formatter={(value) => formatPeso(String(value ?? 0))}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
              <Bar
                dataKey="sales"
                name={filtered ? 'sales · this branch' : 'sales'}
                stackId={stackId}
                fill="hsl(var(--foreground))"
                radius={filtered ? [2, 2, 0, 0] : [0, 0, 0, 0]}
              />
              <Bar
                dataKey="commission"
                name={filtered ? 'commission paid · all branches' : 'commission paid'}
                stackId={stackId}
                fill="hsl(var(--debit))"
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
